/* eslint-disable @typescript-eslint/no-explicit-any */
import { createSlice } from '@reduxjs/toolkit';
import { postPayInvoice, getIsPaid } from './thunk';
import { PayInvoiceResponse } from './types';
import type { RootState } from '@/redux/store';

interface PayInvoiceState {
  loading: boolean;
  error: string | null;
  data: PayInvoiceResponse | null;

  isPaidLoading: boolean;
  isPaidError: string | null;
  isPaid: boolean | null;
  isPaidRaw?: any;
  isPaidMessage?: string;
  lastCheckedNumber?: string;
  lastCheckedAt?: string;
}

const initialState: PayInvoiceState = {
  loading: false,
  error: null,
  data: null,

  isPaidLoading: false,
  isPaidError: null,
  isPaid: null,
  isPaidRaw: undefined,
  isPaidMessage: undefined,
  lastCheckedNumber: undefined,
  lastCheckedAt: undefined,
};

const payInvoiceSlice = createSlice({
  name: 'payInvoice',
  initialState,
  reducers: {
    resetPayInvoice: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(postPayInvoice.pending, (state) => {
        state.loading = true;
        state.error = null;

        // 🔄 When starting a new checkout, clear previous isPaid status/messages
        state.isPaidLoading = false;
        state.isPaidError = null;
        state.isPaid = null;
        state.isPaidMessage = undefined;
        state.isPaidRaw = undefined;
      })
      .addCase(postPayInvoice.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload ?? null;
      })
      .addCase(postPayInvoice.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) ?? 'Something went wrong';
      })

      .addCase(getIsPaid.pending, (state, action) => {
        const arg = (action.meta as any)?.arg;
        const nextNumber = arg?.number ? String(arg.number) : undefined;

        // If checking a different invoice, clear previous result to avoid stale UI
        if (
          nextNumber &&
          state.lastCheckedNumber &&
          nextNumber !== state.lastCheckedNumber
        ) {
          state.isPaid = null;
          state.isPaidMessage = undefined;
          state.isPaidError = null;
          state.isPaidRaw = undefined;
        }

        state.isPaidLoading = true;
        state.isPaidError = null;
        if (nextNumber) state.lastCheckedNumber = nextNumber;
      })
      .addCase(getIsPaid.fulfilled, (state, action) => {
        state.isPaidLoading = false;
        state.isPaid = action.payload.paid;
        state.isPaidMessage = action.payload.message;
        state.isPaidRaw = action.payload.raw;
        state.lastCheckedAt = new Date().toISOString();
      })
      .addCase(getIsPaid.rejected, (state, action) => {
        state.isPaidLoading = false;
        state.isPaidError =
          (action.payload as string) ?? 'Could not check payment status';
        state.lastCheckedAt = new Date().toISOString();
      });
  },
});

export const { resetPayInvoice } = payInvoiceSlice.actions;

export const selectPayInvoiceLoading = (s: RootState) => s.payInvoice.loading;
export const selectPayInvoiceError = (s: RootState) => s.payInvoice.error;
export const selectPayInvoiceData = (s: RootState) => s.payInvoice.data;

export const selectIsPaidLoading = (s: RootState) => s.payInvoice.isPaidLoading;
export const selectIsPaidError = (s: RootState) => s.payInvoice.isPaidError;
export const selectIsPaid = (s: RootState) => s.payInvoice.isPaid;
export const selectIsPaidMessage = (s: RootState) => s.payInvoice.isPaidMessage;
export const selectIsPaidRaw = (s: RootState) => s.payInvoice.isPaidRaw;
export const selectIsPaidLastCheckedNumber = (s: RootState) =>
  s.payInvoice.lastCheckedNumber;
export const selectIsPaidLastCheckedAt = (s: RootState) =>
  s.payInvoice.lastCheckedAt;

/** Optional convenience selector */
export const selectIsPaidStatus = (s: RootState) => ({
  loading: s.payInvoice.isPaidLoading,
  paid: s.payInvoice.isPaid,
  message: s.payInvoice.isPaidMessage,
  error: s.payInvoice.isPaidError,
  lastCheckedNumber: s.payInvoice.lastCheckedNumber,
  lastCheckedAt: s.payInvoice.lastCheckedAt,
});

export default payInvoiceSlice.reducer;
